import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import type { HabitCategory, HabitFrequencyConfig } from '@/types/habits';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface HabitPresetExcelUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (presets: ParsedPreset[]) => Promise<void>;
}

export interface ParsedPreset {
  name: string;
  nameAr?: string;
  category: HabitCategory;
  description?: string;
  descriptionAr?: string;
  isActive: boolean;
  frequencyConfig: HabitFrequencyConfig;
  errors: string[];
}

export function HabitPresetExcelUpload({ open, onOpenChange, onImport }: HabitPresetExcelUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedPresets, setParsedPresets] = useState<ParsedPreset[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t, language, dir } = useLanguage();

  const downloadTemplate = () => {
    const templateData = [
      // Daily - Single time
      {
        'Name (English)': 'Morning Meditation',
        'Name (Arabic)': 'تأمل الصباح',
        'Category': 'Stress',
        'Description (English)': '10 minutes of mindfulness meditation',
        'Description (Arabic)': '10 دقائق من التأمل الواعي',
        'Frequency': 'daily',
        'Times Per Frequency': 1,
        'Duration (Days)': 30,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Daily - Multiple times
      {
        'Name (English)': 'Drink Water',
        'Name (Arabic)': 'شرب الماء',
        'Category': 'Nutrition',
        'Description (English)': 'Drink 8 glasses of water throughout the day',
        'Description (Arabic)': 'اشرب 8 أكواب من الماء طوال اليوم',
        'Frequency': 'daily',
        'Times Per Frequency': 8,
        'Duration (Days)': 30,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Daily - Multiple times (another example)
      {
        'Name (English)': 'Healthy Snack',
        'Name (Arabic)': 'وجبة خفيفة صحية',
        'Category': 'Nutrition',
        'Description (English)': 'Eat a healthy snack between meals',
        'Description (Arabic)': 'تناول وجبة خفيفة صحية بين الوجبات',
        'Frequency': 'daily',
        'Times Per Frequency': 3,
        'Duration (Days)': 60,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Weekly - Single time
      {
        'Name (English)': 'Weekly Meal Prep',
        'Name (Arabic)': 'تحضير الوجبات الأسبوعية',
        'Category': 'Nutrition',
        'Description (English)': 'Prepare healthy meals for the week',
        'Description (Arabic)': 'تحضير وجبات صحية للأسبوع',
        'Frequency': 'weekly',
        'Times Per Frequency': 1,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Weekly - Multiple times
      {
        'Name (English)': 'Gym Workout',
        'Name (Arabic)': 'تمرين الصالة الرياضية',
        'Category': 'Exercises',
        'Description (English)': 'Strength training at the gym',
        'Description (Arabic)': 'تمارين القوة في الصالة الرياضية',
        'Frequency': 'weekly',
        'Times Per Frequency': 3,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Monthly - Single time
      {
        'Name (English)': 'Health Check-up',
        'Name (Arabic)': 'الفحص الصحي',
        'Category': 'Exercises',
        'Description (English)': 'Monthly health assessment and measurements',
        'Description (Arabic)': 'التقييم الصحي والقياسات الشهرية',
        'Frequency': 'monthly',
        'Times Per Frequency': 1,
        'Duration (Days)': 180,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Monthly - Multiple times
      {
        'Name (English)': 'Social Activity',
        'Name (Arabic)': 'نشاط اجتماعي',
        'Category': 'Relations',
        'Description (English)': 'Spend quality time with friends or family',
        'Description (Arabic)': 'قضاء وقت ممتع مع الأصدقاء أو العائلة',
        'Frequency': 'monthly',
        'Times Per Frequency': 2,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Custom - Monday, Wednesday, Friday
      {
        'Name (English)': 'Running',
        'Name (Arabic)': 'الجري',
        'Category': 'Exercises',
        'Description (English)': 'Go for a 30-minute run',
        'Description (Arabic)': 'اذهب للجري لمدة 30 دقيقة',
        'Frequency': 'custom',
        'Times Per Frequency': 1,
        'Duration (Days)': 60,
        'Custom Days (0=Sun,1=Mon...)': '1,3,5',
        'Is Active': 'TRUE'
      },
      // Custom - Weekdays only (Mon-Fri)
      {
        'Name (English)': 'Work Break Stretch',
        'Name (Arabic)': 'تمدد في استراحة العمل',
        'Category': 'Exercises',
        'Description (English)': 'Take a stretch break during work',
        'Description (Arabic)': 'خذ استراحة تمدد خلال العمل',
        'Frequency': 'custom',
        'Times Per Frequency': 2,
        'Duration (Days)': 45,
        'Custom Days (0=Sun,1=Mon...)': '1,2,3,4,5',
        'Is Active': 'TRUE'
      },
      // Custom - Weekends only (Sat-Sun)
      {
        'Name (English)': 'Family Time',
        'Name (Arabic)': 'وقت العائلة',
        'Category': 'Relations',
        'Description (English)': 'Quality time with family on weekends',
        'Description (Arabic)': 'وقت ممتع مع العائلة في عطلة نهاية الأسبوع',
        'Frequency': 'custom',
        'Times Per Frequency': 1,
        'Duration (Days)': 60,
        'Custom Days (0=Sun,1=Mon...)': '0,6',
        'Is Active': 'TRUE'
      },
      // Custom - Every other day (Mon, Wed, Fri, Sun)
      {
        'Name (English)': 'Yoga Practice',
        'Name (Arabic)': 'ممارسة اليوغا',
        'Category': 'Stress',
        'Description (English)': 'Yoga session for flexibility and relaxation',
        'Description (Arabic)': 'جلسة يوغا للمرونة والاسترخاء',
        'Frequency': 'custom',
        'Times Per Frequency': 1,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '0,1,3,5',
        'Is Active': 'TRUE'
      },
      // Sleep habit - Daily
      {
        'Name (English)': 'Sleep 8 Hours',
        'Name (Arabic)': 'النوم 8 ساعات',
        'Category': 'Sleep',
        'Description (English)': 'Get at least 8 hours of quality sleep',
        'Description (Arabic)': 'احصل على 8 ساعات من النوم الجيد على الأقل',
        'Frequency': 'daily',
        'Times Per Frequency': 1,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Addiction control - Daily
      {
        'Name (English)': 'No Smoking',
        'Name (Arabic)': 'لا تدخين',
        'Category': 'Addictions',
        'Description (English)': 'Stay smoke-free for the entire day',
        'Description (Arabic)': 'ابق بعيداً عن التدخين طوال اليوم',
        'Frequency': 'daily',
        'Times Per Frequency': 1,
        'Duration (Days)': 90,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Long duration example
      {
        'Name (English)': 'Year-Long Fitness Journey',
        'Name (Arabic)': 'رحلة اللياقة السنوية',
        'Category': 'Exercises',
        'Description (English)': 'Commit to daily exercise for a full year',
        'Description (Arabic)': 'التزم بالتمارين اليومية لمدة عام كامل',
        'Frequency': 'daily',
        'Times Per Frequency': 1,
        'Duration (Days)': 365,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      },
      // Short duration example
      {
        'Name (English)': 'Weekly Challenge',
        'Name (Arabic)': 'التحدي الأسبوعي',
        'Category': 'Exercises',
        'Description (English)': '7-day fitness challenge',
        'Description (Arabic)': 'تحدي لياقة لمدة 7 أيام',
        'Frequency': 'daily',
        'Times Per Frequency': 1,
        'Duration (Days)': 7,
        'Custom Days (0=Sun,1=Mon...)': '',
        'Is Active': 'TRUE'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 30 }, // Name (English)
      { wch: 30 }, // Name (Arabic)
      { wch: 15 }, // Category
      { wch: 45 }, // Description (English)
      { wch: 45 }, // Description (Arabic)
      { wch: 12 }, // Frequency
      { wch: 20 }, // Times Per Frequency
      { wch: 18 }, // Duration (Days)
      { wch: 30 }, // Custom Days
      { wch: 12 }  // Is Active
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Habit Presets Template');
    
    // Add instructions sheet
    const instructions = [
      ['Habit Preset Upload Template - Comprehensive Guide'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['REQUIRED COLUMNS'],
      ['═══════════════════════════════════════════════════════════════════'],
      [''],
      ['1. Name (English) - English name of the habit preset (Required)'],
      ['   Example: "Morning Exercise", "Drink Water"'],
      [''],
      ['2. Category - Must be one of: (Required)'],
      ['   • Exercises - Physical activities, workouts, movement'],
      ['   • Nutrition - Diet, food, hydration habits'],
      ['   • Sleep - Sleep quality, duration, routine'],
      ['   • Stress - Meditation, relaxation, stress management'],
      ['   • Relations - Social interactions, relationships, family time'],
      ['   • Addictions - Habits to overcome addictions'],
      [''],
      ['3. Frequency - Type of frequency (Required)'],
      ['   • daily - Habit to be done every day'],
      ['   • weekly - Habit to be done weekly'],
      ['   • monthly - Habit to be done monthly'],
      ['   • custom - Habit to be done on specific days of the week'],
      [''],
      ['4. Times Per Frequency - How many times to perform (Required)'],
      ['   • Must be between 1 and 100'],
      ['   • Examples: 1 (once), 3 (three times), 8 (eight times)'],
      [''],
      ['5. Duration (Days) - Total duration in days (Required)'],
      ['   • Must be between 1 and 365 days'],
      ['   • Examples: 7 (one week), 30 (one month), 90 (three months), 365 (one year)'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['OPTIONAL COLUMNS'],
      ['═══════════════════════════════════════════════════════════════════'],
      [''],
      ['6. Name (Arabic) - Arabic name of the habit preset (Optional)'],
      ['   Example: "تمرين الصباح", "شرب الماء"'],
      [''],
      ['7. Description (English) - English description (Optional)'],
      ['   Example: "30 minutes of cardio or strength training"'],
      [''],
      ['8. Description (Arabic) - Arabic description (Optional)'],
      ['   Example: "30 دقيقة من تمارين القلب أو القوة"'],
      [''],
      ['9. Custom Days - For custom frequency only (Optional but required for custom)'],
      ['   • Comma-separated day numbers: 0=Sunday, 1=Monday, 2=Tuesday, etc.'],
      ['   • Examples:'],
      ['     - "1,3,5" = Monday, Wednesday, Friday'],
      ['     - "1,2,3,4,5" = Weekdays (Mon-Fri)'],
      ['     - "0,6" = Weekends (Sat-Sun)'],
      ['     - "0,1,3,5" = Sun, Mon, Wed, Fri'],
      [''],
      ['10. Is Active - Whether preset is active (Optional, defaults to TRUE)'],
      ['    • TRUE or YES or 1 = Active'],
      ['    • FALSE or NO or 0 = Inactive'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['FREQUENCY EXAMPLES IN THE TEMPLATE'],
      ['═══════════════════════════════════════════════════════════════════'],
      [''],
      ['DAILY HABITS:'],
      ['─────────────'],
      ['• Row 1: Morning Meditation - Once per day for 30 days'],
      ['• Row 2: Drink Water - 8 times per day for 30 days'],
      ['• Row 3: Healthy Snack - 3 times per day for 60 days'],
      ['• Row 12: Sleep 8 Hours - Once per day for 90 days'],
      ['• Row 13: No Smoking - Once per day for 90 days'],
      ['• Row 14: Year-Long Fitness - Once per day for 365 days'],
      ['• Row 15: Weekly Challenge - Once per day for 7 days'],
      [''],
      ['WEEKLY HABITS:'],
      ['──────────────'],
      ['• Row 4: Weekly Meal Prep - Once per week for 90 days'],
      ['• Row 5: Gym Workout - 3 times per week for 90 days'],
      [''],
      ['MONTHLY HABITS:'],
      ['───────────────'],
      ['• Row 6: Health Check-up - Once per month for 180 days'],
      ['• Row 7: Social Activity - 2 times per month for 90 days'],
      [''],
      ['CUSTOM HABITS (Specific Days):'],
      ['───────────────────────────────'],
      ['• Row 8: Running - Mon, Wed, Fri (1,3,5) once per occurrence for 60 days'],
      ['• Row 9: Work Break Stretch - Weekdays (1,2,3,4,5) twice per day for 45 days'],
      ['• Row 10: Family Time - Weekends (0,6) once per occurrence for 60 days'],
      ['• Row 11: Yoga Practice - Sun, Mon, Wed, Fri (0,1,3,5) once per occurrence for 90 days'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['VALIDATION RULES'],
      ['═══════════════════════════════════════════════════════════════════'],
      [''],
      ['✓ Name (English) cannot be empty'],
      ['✓ Category must be exactly one of the listed categories (case-sensitive)'],
      ['✓ Frequency must be exactly: daily, weekly, monthly, or custom (lowercase)'],
      ['✓ Times Per Frequency must be a number between 1 and 100'],
      ['✓ Duration must be a number between 1 and 365'],
      ['✓ Custom Days are required when Frequency is "custom"'],
      ['✓ Custom Days must be comma-separated numbers between 0 and 6'],
      ['✓ All rows with errors will be highlighted and not imported'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['TIPS FOR SUCCESS'],
      ['═══════════════════════════════════════════════════════════════════'],
      [''],
      ['1. Start with the provided examples and modify them'],
      ['2. Keep category names exactly as specified (case-sensitive)'],
      ['3. Use lowercase for frequency values: daily, weekly, monthly, custom'],
      ['4. For custom habits, always specify the Custom Days column'],
      ['5. Day numbers: 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday,'],
      ['   4=Thursday, 5=Friday, 6=Saturday'],
      ['6. You can mix different frequency types in the same file'],
      ['7. The system will validate and show errors before importing'],
      ['8. Only valid presets will be imported - invalid ones will be skipped'],
      [''],
      ['═══════════════════════════════════════════════════════════════════'],
      ['Need help? Check the example rows in the main sheet for reference!'],
      ['═══════════════════════════════════════════════════════════════════']
    ];
    
    const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
    instructionSheet['!cols'] = [{ wch: 100 }];
    XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');
    
    XLSX.writeFile(workbook, 'habit_presets_template.xlsx');
    toast.success(language === 'ar' ? 'تم تنزيل القالب بنجاح' : 'Template downloaded successfully');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = async (file: File) => {
    setLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      if (jsonData.length < 2) {
        throw new Error('Excel file must have at least a header row and one data row');
      }

      const headers = jsonData[0] as string[];
      const dataRows = jsonData.slice(1);

      // Validate required headers
      const requiredHeaders = ['Name (English)', 'Category', 'Frequency', 'Times Per Frequency', 'Duration (Days)'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
      }

      const parsed: ParsedPreset[] = [];
      const validCategories: HabitCategory[] = ['Exercises', 'Nutrition', 'Sleep', 'Stress', 'Relations', 'Addictions'];
      const validFrequencies = ['daily', 'weekly', 'monthly', 'custom'];

      dataRows.forEach((row, rowIndex) => {
        const preset: ParsedPreset = {
          name: '',
          category: 'Exercises',
          isActive: true,
          frequencyConfig: {
            frequency: 'daily',
            timesPerFrequency: 1,
            duration: 90
          },
          errors: []
        };

        // Parse each column
        headers.forEach((header, colIndex) => {
          const value = row[colIndex];
          
          switch (header) {
            case 'Name (English)':
              if (!value || String(value).trim() === '') {
                preset.errors.push('Name (English) is required');
              } else {
                preset.name = String(value).trim();
              }
              break;
              
            case 'Name (Arabic)':
              if (value && String(value).trim() !== '') {
                preset.nameAr = String(value).trim();
              }
              break;
              
            case 'Category':
              if (!value || String(value).trim() === '') {
                preset.errors.push('Category is required');
              } else {
                const category = String(value).trim();
                if (!validCategories.includes(category as HabitCategory)) {
                  preset.errors.push(`Invalid category: ${category}. Must be one of: ${validCategories.join(', ')}`);
                } else {
                  preset.category = category as HabitCategory;
                }
              }
              break;
              
            case 'Description (English)':
              if (value && String(value).trim() !== '') {
                preset.description = String(value).trim();
              }
              break;
              
            case 'Description (Arabic)':
              if (value && String(value).trim() !== '') {
                preset.descriptionAr = String(value).trim();
              }
              break;
              
            case 'Frequency':
              if (!value || String(value).trim() === '') {
                preset.errors.push('Frequency is required');
              } else {
                const frequency = String(value).trim().toLowerCase();
                if (!validFrequencies.includes(frequency)) {
                  preset.errors.push(`Invalid frequency: ${frequency}. Must be one of: ${validFrequencies.join(', ')}`);
                } else {
                  preset.frequencyConfig.frequency = frequency as any;
                }
              }
              break;
              
            case 'Times Per Frequency':
              const times = Number(value);
              if (isNaN(times) || times < 1 || times > 100) {
                preset.errors.push('Times Per Frequency must be between 1 and 100');
              } else {
                preset.frequencyConfig.timesPerFrequency = times;
              }
              break;
              
            case 'Duration (Days)':
              const duration = Number(value);
              if (isNaN(duration) || duration < 1 || duration > 365) {
                preset.errors.push('Duration must be between 1 and 365 days');
              } else {
                preset.frequencyConfig.duration = duration;
              }
              break;
              
            case 'Custom Days (0=Sun,1=Mon...)':
              if (value && String(value).trim() !== '') {
                try {
                  const days = String(value).split(',').map(d => {
                    const day = Number(d.trim());
                    if (isNaN(day) || day < 0 || day > 6) {
                      throw new Error('Invalid day');
                    }
                    return day;
                  });
                  preset.frequencyConfig.customDays = days;
                } catch {
                  preset.errors.push('Custom Days must be comma-separated numbers (0-6)');
                }
              }
              break;
              
            case 'Is Active':
              if (value !== undefined && value !== null) {
                const activeStr = String(value).trim().toUpperCase();
                preset.isActive = activeStr === 'TRUE' || activeStr === '1' || activeStr === 'YES';
              }
              break;
          }
        });

        // Validate custom frequency has custom days
        if (preset.frequencyConfig.frequency === 'custom' && (!preset.frequencyConfig.customDays || preset.frequencyConfig.customDays.length === 0)) {
          preset.errors.push('Custom frequency requires Custom Days to be specified');
        }

        parsed.push(preset);
      });

      setParsedPresets(parsed);
      
      const hasErrors = parsed.some(p => p.errors.length > 0);
      if (hasErrors) {
        toast.warning(language === 'ar' 
          ? 'تم تحليل الملف مع بعض الأخطاء. يرجى مراجعتها قبل الاستيراد.' 
          : 'File parsed with some errors. Please review before importing.');
      } else {
        toast.success(language === 'ar' 
          ? `تم تحليل ${parsed.length} عادة بنجاح` 
          : `Successfully parsed ${parsed.length} presets`);
      }
    } catch (error: any) {
      console.error('Error parsing Excel file:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل تحليل الملف' : 'Failed to parse file'));
      setParsedPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const validPresets = parsedPresets.filter(p => p.errors.length === 0);
    
    if (validPresets.length === 0) {
      toast.error(language === 'ar' 
        ? 'لا توجد عادات صالحة للاستيراد' 
        : 'No valid presets to import');
      return;
    }

    setImporting(true);
    try {
      await onImport(validPresets);
      toast.success(language === 'ar' 
        ? `تم استيراد ${validPresets.length} عادة بنجاح` 
        : `Successfully imported ${validPresets.length} presets`);
      onOpenChange(false);
      setFile(null);
      setParsedPresets([]);
    } catch (error) {
      console.error('Error importing presets:', error);
      toast.error(language === 'ar' ? 'فشل الاستيراد' : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setParsedPresets([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" dir={dir}>
        <DialogHeader>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {language === 'ar' ? 'استيراد العادات من Excel' : 'Import Habit Presets from Excel'}
          </DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {language === 'ar' 
              ? 'قم بتنزيل القالب، املأه بالعادات، ثم قم بتحميله هنا.' 
              : 'Download the template, fill it with presets, and upload it here.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-auto">
          {/* Download Template */}
          <div className="flex gap-2">
            <Button onClick={downloadTemplate} variant="outline" className="flex-1">
              <Download className={cn("h-4 w-4", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
              {language === 'ar' ? 'تنزيل القالب' : 'Download Template'}
            </Button>
          </div>

          {/* Upload File */}
          <div className="space-y-2">
            <Label htmlFor="excel-file" className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {language === 'ar' ? 'تحميل ملف Excel' : 'Upload Excel File'}
            </Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                id="excel-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={loading || importing}
                className="flex-1"
              />
              {file && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setFile(null);
                    setParsedPresets([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={loading || importing}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-primary animate-pulse" />
                <p className="text-muted-foreground">
                  {language === 'ar' ? 'جاري تحليل الملف...' : 'Parsing file...'}
                </p>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {!loading && parsedPresets.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-4 py-2 flex items-center justify-between">
                <h3 className="font-medium">
                  {language === 'ar' 
                    ? `معاينة (${parsedPresets.length} عادة)` 
                    : `Preview (${parsedPresets.length} presets)`}
                </h3>
                <div className="flex gap-2 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    {parsedPresets.filter(p => p.errors.length === 0).length} {language === 'ar' ? 'صالح' : 'valid'}
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    {parsedPresets.filter(p => p.errors.length > 0).length} {language === 'ar' ? 'خطأ' : 'errors'}
                  </span>
                </div>
              </div>
              
              <div className="max-h-96 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                      <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'الاسم' : 'Name'}
                      </TableHead>
                      <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'الفئة' : 'Category'}
                      </TableHead>
                      <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'التكرار' : 'Frequency'}
                      </TableHead>
                      <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'المدة' : 'Duration'}
                      </TableHead>
                      <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'الحالة' : 'Status'}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedPresets.map((preset, index) => (
                      <TableRow key={index} className={preset.errors.length > 0 ? 'bg-red-50' : ''}>
                        <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                          <div>
                            <div className="font-medium">{preset.name}</div>
                            {preset.nameAr && (
                              <div className="text-xs text-muted-foreground">{preset.nameAr}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                          {preset.category}
                        </TableCell>
                        <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                          {preset.frequencyConfig.frequency} ({preset.frequencyConfig.timesPerFrequency}x)
                        </TableCell>
                        <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                          {preset.frequencyConfig.duration} {language === 'ar' ? 'يوم' : 'days'}
                        </TableCell>
                        <TableCell className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                          {preset.errors.length === 0 ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <CheckCircle className={cn("h-3 w-3", dir === 'rtl' ? 'ml-1' : 'mr-1')} />
                              {language === 'ar' ? 'صالح' : 'Valid'}
                            </Badge>
                          ) : (
                            <div className="space-y-1">
                              <Badge variant="outline" className="bg-red-50 text-red-700">
                                <AlertCircle className={cn("h-3 w-3", dir === 'rtl' ? 'ml-1' : 'mr-1')} />
                                {language === 'ar' ? 'خطأ' : 'Error'}
                              </Badge>
                              <ul className="text-xs text-red-600 space-y-0.5">
                                {preset.errors.map((error, i) => (
                                  <li key={i}>• {error}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className={cn("gap-2", dir === 'rtl' ? 'flex-row-reverse' : '')}>
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedPresets.length === 0 || parsedPresets.every(p => p.errors.length > 0) || importing}
          >
            {importing ? (
              <>
                <Upload className={cn("h-4 w-4 animate-pulse", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
                {language === 'ar' ? 'جاري الاستيراد...' : 'Importing...'}
              </>
            ) : (
              <>
                <Upload className={cn("h-4 w-4", dir === 'rtl' ? 'ml-2' : 'mr-2')} />
                {language === 'ar' 
                  ? `استيراد ${parsedPresets.filter(p => p.errors.length === 0).length} عادة` 
                  : `Import ${parsedPresets.filter(p => p.errors.length === 0).length} Presets`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

